import { supabase } from '../lib/supabase';
import { errorHandler } from '../utils/errorHandler';
import { validationUtils } from '../utils/validation';

export const leadService = {
  async getLeads(instituteId, filters = {}) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      let query = supabase?.from('leads')?.select(`
        *,
        student:student_id(id, name, email, phone, city, state),
        institute:institute_id(id, name, email)
      `)?.eq('institute_id', instituteId);
    
      if (filters?.tag && filters?.tag !== 'all') {
        query = query?.eq('lead_status', filters?.tag);
      }
      
      if (filters?.source && filters?.source !== 'all') {
        query = query?.eq('source', filters?.source);
      }
      
      if (filters?.search) {
        const sanitizedSearch = validationUtils?.sanitizeString(filters?.search);
        query = query?.or(`student.name.ilike.%${sanitizedSearch}%,student.email.ilike.%${sanitizedSearch}%`);
      }
      
      query = query?.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return data?.map(lead => ({
        id: lead?.id,
        studentId: lead?.student_id,
        instituteId: lead?.institute_id,
        source: lead?.source,
        leadStatus: lead?.lead_status,
        notes: lead?.notes,
        followUpDate: lead?.follow_up_date,
        courseInterest: lead?.course_interest,
        consentGiven: lead?.consent_given,
        createdAt: lead?.created_at,
        updatedAt: lead?.updated_at,
        student: lead?.student,
        institute: lead?.institute
      })) || [];
    }, 'getLeads');
  },

  async getLeadsThisWeek(instituteId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      const weekAgo = new Date();
      weekAgo?.setDate(weekAgo?.getDate() - 7);
      
      const { count, error } = await supabase
        ?.from('leads')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('institute_id', instituteId)
        ?.gte('created_at', weekAgo?.toISOString());
      
      if (error) throw error;
      return count || 0;
    }, 'getLeadsThisWeek');
  },

  async getCourseInterestStats(instituteId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      const { data, error } = await supabase
        ?.from('leads')
        ?.select('course_interest')
        ?.eq('institute_id', instituteId)
        ?.not('course_interest', 'is', null);
      
      if (error) throw error;
      
      const stats = {};
      data?.forEach(lead => {
        const course = lead?.course_interest;
        if (course) {
          stats[course] = (stats?.[course] || 0) + 1;
        }
      });
      
      return Object.entries(stats)?.map(([course, count]) => ({ course, count }))?.sort((a, b) => b?.count - a?.count);
    }, 'getCourseInterestStats');
  },

  async createLead(leadData) {
    return errorHandler?.handleAsync(async () => {
      if (leadData?.studentId && !validationUtils?.isValidUUID(leadData?.studentId)) {
        throw new Error('Invalid student ID');
      }
      if (!validationUtils?.isValidUUID(leadData?.instituteId)) {
        throw new Error('Invalid institute ID');
      }
      
      const sanitizedNotes = leadData?.notes ? validationUtils?.sanitizeString(leadData?.notes) : null;
      const sanitizedCourseInterest = leadData?.courseInterest ? validationUtils?.sanitizeString(leadData?.courseInterest) : null;
      
      const { data, error } = await supabase?.from('leads')?.insert({
        student_id: leadData?.studentId || null,
        institute_id: leadData?.instituteId,
        source: validationUtils?.sanitizeString(leadData?.source || 'booth_visit'),
        lead_status: leadData?.leadStatus || 'warm',
        notes: sanitizedNotes,
        course_interest: sanitizedCourseInterest,
        consent_given: leadData?.consentGiven ?? false
      })?.select()?.single();
      
      if (error) {
        // Ignore duplicate lead errors (unique constraint violation)
        if (error?.code === '23505') {
          return null;
        }
        throw error;
      }
      return data;
    }, 'createLead');
  },

  // Auto-capture lead silently (non-blocking, no error thrown to user)
  async captureLeadSilently(studentId, instituteId, source, courseInterest = null, consentGiven = false) {
    try {
      if (!studentId || !instituteId) return null;
      if (!validationUtils?.isValidUUID(studentId)) return null;
      if (!validationUtils?.isValidUUID(instituteId)) return null;
      
      const { data, error } = await supabase?.from('leads')?.insert({
        student_id: studentId,
        institute_id: instituteId,
        source: source || 'booth_visit',
        lead_status: 'warm',
        course_interest: courseInterest,
        consent_given: consentGiven
      })?.select()?.single();
      
      if (error) {
        // Silently ignore duplicate leads
        if (error?.code === '23505') return null;
        console.warn('[leadService] Silent lead capture failed:', error?.message);
        return null;
      }
      return data;
    } catch (err) {
      console.warn('[leadService] Silent lead capture error:', err?.message);
      return null;
    }
  },

  async updateLead(leadId, updates) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(leadId)) {
        throw new Error('Invalid lead ID');
      }
      
      const dbUpdates = {};
      if (updates?.leadStatus) dbUpdates.lead_status = validationUtils?.sanitizeString(updates?.leadStatus);
      if (updates?.notes) dbUpdates.notes = validationUtils?.sanitizeString(updates?.notes);
      if (updates?.courseInterest !== undefined) dbUpdates.course_interest = updates?.courseInterest ? validationUtils?.sanitizeString(updates?.courseInterest) : null;
      if (updates?.consentGiven !== undefined) dbUpdates.consent_given = updates?.consentGiven;
      if (updates?.followUpDate) {
        if (!validationUtils?.isFutureDate(updates?.followUpDate)) {
          throw new Error('Follow-up date must be in the future');
        }
        dbUpdates.follow_up_date = updates?.followUpDate;
      }
      
      const { data, error } = await supabase?.from('leads')?.update(dbUpdates)?.eq('id', leadId)?.select()?.single();
      
      if (error) throw error;
      return data;
    }, 'updateLead');
  },

  async deleteLead(leadId) {
    return errorHandler?.handleAsync(async () => {
      if (!validationUtils?.isValidUUID(leadId)) {
        throw new Error('Invalid lead ID');
      }
      
      const { error } = await supabase?.from('leads')?.delete()?.eq('id', leadId);
      
      if (error) throw error;
    }, 'deleteLead');
  }
};